import React from 'react';
import { Check } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';

interface FormInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'url' | 'date';
  pattern?: string;
  disabled?: boolean;
  dataTestId?: string;
  className?: string;
  icon?: React.ReactNode;
  maxLength?: number;
  minLength?: number;
  /** Bornes pour les inputs type="date"/"number" (format YYYY-MM-DD pour les dates). */
  min?: string;
  max?: string;
  autoComplete?: string;
  helpText?: string;
  suffix?: string;
  readOnly?: boolean;
  valid?: boolean;
  size?: 'default' | 'sm';
  tooltip?: string;
  onBlur?: () => void;
}

export const FormInput: React.FC<FormInputProps> = ({
  value,
  onChange,
  label,
  placeholder,
  required = false,
  error,
  type = 'text',
  pattern,
  disabled = false,
  dataTestId,
  className = '',
  icon,
  maxLength,
  minLength,
  min,
  max,
  autoComplete,
  helpText,
  suffix,
  readOnly = false,
  valid,
  size = 'default',
  tooltip,
  onBlur,
}) => {
  const isDateType = type === 'date';
  const hasValue = !!value;
  // For date inputs, always show label in "up" position
  const alwaysFloated = isDateType;

  const sm = size === 'sm';
  const textClass = sm ? 'tw-text-sm' : 'tw-text-base';
  const paddingClass = label ? (sm ? 'tw-pt-4 tw-pb-1' : 'tw-pt-5 tw-pb-1.5') : (sm ? 'tw-py-1' : 'tw-py-2.5');
  const labelFloated = sm ? 'tw-top-1 tw-text-[10px]' : 'tw-top-1.5 tw-text-[11px]';
  const labelUnfloated = sm
    ? 'tw-top-[9px] tw-text-sm tw-text-gray-400 peer-focus:tw-top-1 peer-focus:tw-text-[10px]'
    : 'tw-top-[13px] tw-text-base tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px]';

  return (
    <div className={className}>
      <div className="tw-relative">
        {icon && (
          <div className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-z-10">
            {icon}
          </div>
        )}

        <input
          type={type}
          data-testid={dataTestId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onWheel={type === 'number' ? (e) => (e.target as HTMLInputElement).blur() : undefined}
          required={required}
          placeholder={label || placeholder || ' '}
          pattern={pattern}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          minLength={minLength}
          min={min}
          max={max}
          autoComplete={autoComplete}
          className={`tw-w-full ${icon ? 'tw-pl-10' : 'tw-pl-3'} ${suffix ? 'tw-pr-12' : valid ? 'tw-pr-9' : 'tw-pr-3'} ${paddingClass} ${textClass} tw-border ${sm ? 'tw-rounded-lg' : 'tw-rounded-xl'} tw-transition-all tw-peer tw-placeholder-transparent
            ${error ? 'tw-border-red-500' : valid ? 'tw-border-green-400' : 'tw-border-gray-200'}
            focus:tw-outline-none focus:tw-ring-2
            ${error ? 'focus:tw-ring-red-500' : 'focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary'}
            disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed disabled:tw-text-gray-500`}
        />

        {label && (
          <label
            className={`tw-absolute ${icon ? 'tw-left-10' : 'tw-left-3'} tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
              ${alwaysFloated || hasValue
                ? labelFloated
                : labelUnfloated
              }
              ${error
                ? (alwaysFloated || hasValue ? 'tw-text-red-500' : 'peer-focus:tw-text-red-500')
                : (alwaysFloated || hasValue ? 'tw-text-affinicar-primary' : 'peer-focus:tw-text-affinicar-primary')
              }
            `}
          >
            {label}
            {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
            {tooltip && <Tooltip content={tooltip} />}
          </label>
        )}

        {suffix && (
          <span className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-text-sm tw-pointer-events-none">
            {suffix}
          </span>
        )}

        {valid && !error && !suffix && (
          <div className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-pointer-events-none">
            <Check className="tw-w-4 tw-h-4 tw-text-green-500" />
          </div>
        )}
      </div>

      {helpText && !error && (
        <p className="tw-mt-1 tw-text-sm tw-text-gray-500">{helpText}</p>
      )}
      {error && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>
      )}
    </div>
  );
};
